import React, { FC } from 'react';
import {
  Typography,
  Stack,
  Divider,
  ButtonBase,
  CircularProgress,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import Popup from 'src/components/content/popup/popup';
import ContentWrapper from 'src/components/content/content-wrapper';
import InfoIcon from 'src/assets/icons/infoGreen.svg';
import AfterpayIcon from 'src/assets/icons/afterpay.svg';
import { PaymentMethod, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import styles from './modal.module.scss';
import ContentSection from 'src/components/content/content-section';
import colors from 'src/styles/equalution/colors';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { Button } from 'reactstrap';
import { setIsPersonalCoachAddonAdded } from 'src/store/payment';
import { trackAddonSelected, trackAddonRemoved } from 'src/util/track';
import { ChallengePlanId } from 'src/pages/types';
import { InfoBox } from 'src/components/info-box/InfoBox';
import { AddOnPersonalCoachCard } from '../components/PersonalCoachAddonCard';
import { getChallengeIncludedItems } from '../constants';
import { useChallengeProducts } from 'src/pages/subscription-new/hooks/useChallengeProducts';
import PaymentPopup from 'src/pages/account/sections/subscription/update-payment/update-payment-popup';
import { PaymentMethodSelector } from '../components/PaymentMethodSelector';
import { usePaymentMethodState } from '../hooks/usePaymentMethodState';
import { getCurrencySymbol } from 'src/lib/utils';
import { isCardPaymentMethod } from '../utils/challengeAddon.util';

interface Props {
  onClose: () => void;
  handleCTAClick: () => void;
  isLoading: boolean;
  paymentMethod?: PaymentMethod;
  stripePromise: Promise<Stripe | null>;
  setPaymentMethod: (paymentMethod: PaymentMethod) => void;
  onPaymentTypeChange?: (type: 'card' | 'afterpay') => void;
  hidePersonalCoachAddon?: boolean;
}

export const ChallengeAddOnSummaryModal: FC<Props> = ({
  onClose,
  handleCTAClick,
  isLoading,
  paymentMethod,
  stripePromise,
  setPaymentMethod,
  onPaymentTypeChange,
  hidePersonalCoachAddon = false,
}) => {
  const dispatch = useAppDispatch();

  const {
    challengeAddOnProduct,
    isPersonalCoachingAdded,
    personalCoachPriceAmount,
  } = useAppSelector(state => state.payment);

  const { user } = useAppSelector(state => state.auth);

  const challengeOnly =
    challengeAddOnProduct?.challengeId === ChallengePlanId.ONLY_CHALLENGE;
  const isPersonalCoach =
    challengeAddOnProduct?.challengeId === ChallengePlanId.PERSON_COACH;

  const isPurchaseAllowed = challengeOnly || isPersonalCoach;

  const {
    selectedPaymentType,
    paymentPopupVisible,
    setPaymentPopupVisible,
    handlePaymentTypeChange,
    handlePaymentMethodUpdate,
    handleAddCardClick,
  } = usePaymentMethodState({
    initialPaymentMethod: paymentMethod,
    onPaymentTypeChange,
    shouldNotifyParent: isPurchaseAllowed,
  });
  const { challengeData } = useChallengeProducts();

  const includedItems = getChallengeIncludedItems(
    challengeAddOnProduct?.challengeId,
    challengeData?.asset_metadata
  );

  const total =
    (challengeAddOnProduct?.amount ?? 0) +
    (isPersonalCoachingAdded ? personalCoachPriceAmount ?? 0 : 0);

  return (
    <>
      <Popup onClose={onClose}>
        <ContentWrapper className={styles.background}>
          <ContentSection className={styles.content} collapseAt="sm">
            <Button className={styles.close} onClick={onClose} />

            <Typography
              mt={3}
              fontSize={32}
              fontWeight={700}
              color={colors.SALAD}
              lineHeight={'42px'}
              textAlign="center"
              paddingX={'32px'}
              gutterBottom>
              {challengeAddOnProduct?.product?.name}
            </Typography>

            {!isPersonalCoach && (
              <>
                <Typography
                  color={colors.BLUEBERRY}
                  fontWeight={600}
                  textAlign="center"
                  sx={{ textDecoration: 'underline', mb: 2 }}>
                  What’s included
                </Typography>

                <Stack spacing={1.5} sx={{ mb: 5 }}>
                  {includedItems.map(item => (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      key={item}>
                      <CheckIcon
                        sx={{ color: colors.SALAD, marginRight: '10px' }}
                      />
                      <Typography color={colors.OFF_BLACK} fontWeight={600}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}

            {!hidePersonalCoachAddon && (
              <AddOnPersonalCoachCard
                isLoading={isLoading}
                price={personalCoachPriceAmount?.toFixed(0)}
                isPersonalCoachingAdded={
                  isPersonalCoachingAdded || isPersonalCoach
                }
                onClick={() => {
                  if (isLoading) {
                    return;
                  }
                  if (isPersonalCoachingAdded) {
                    trackAddonRemoved('personal_nutrition_coach');
                  } else {
                    trackAddonSelected(
                      'personal_nutrition_coach',
                      selectedPaymentType,
                      personalCoachPriceAmount || 0
                    );
                  }
                  dispatch(
                    setIsPersonalCoachAddonAdded(!isPersonalCoachingAdded)
                  );
                }}
              />
            )}

            {isPurchaseAllowed && (
              <>
                <Divider sx={{ my: 2 }} />
                <PaymentMethodSelector
                  isLoading={isLoading}
                  selectedPaymentType={selectedPaymentType}
                  onPaymentTypeChange={handlePaymentTypeChange}
                  paymentMethod={paymentMethod}
                  onAddCardClick={handleAddCardClick}
                />
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {isPersonalCoachingAdded && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}>
                <Typography>1:1 coaching support</Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <ButtonBase
                    onClick={() => {
                      if (isLoading) {
                        return;
                      }
                      trackAddonRemoved('personal_nutrition_coach');
                      dispatch(setIsPersonalCoachAddonAdded(false));
                    }}>
                    <Typography
                      variant="button"
                      sx={{
                        fontSize: '14px',
                        textTransform: 'unset',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        color: colors.SALAD,
                        fontWeight: 600,
                        textAlign: 'right',
                      }}>
                      Remove
                    </Typography>
                  </ButtonBase>
                  <Typography
                    fontSize={20}
                    fontWeight={600}
                    color={colors.SALAD}>
                    ${personalCoachPriceAmount?.toFixed(0)}
                  </Typography>
                </Stack>
              </Stack>
            )}

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 3 }}>
              <Typography fontWeight={600}>Total</Typography>
              <Typography fontWeight={600} fontSize={20} color={colors.SALAD}>
                {getCurrencySymbol()}
                {total.toFixed(0)}
              </Typography>
            </Stack>

            <Stack alignItems={'center'}>
              <Button
                disabled={
                  isLoading ||
                  (isPurchaseAllowed &&
                    selectedPaymentType === 'card' &&
                    !isCardPaymentMethod(paymentMethod))
                }
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
                onClick={handleCTAClick}>
                <>
                  {isLoading ? (
                    <CircularProgress
                      color="success"
                      size={30}
                      sx={{ color: colors.WHITE }}
                    />
                  ) : isPurchaseAllowed ? (
                    <>
                      {selectedPaymentType === 'afterpay' && (
                        <img
                          src={AfterpayIcon}
                          alt="Afterpay"
                          style={{ width: '20px', height: '20px' }}
                        />
                      )}
                      Finalise payment
                    </>
                  ) : (
                    'Next'
                  )}
                </>
              </Button>
            </Stack>

            <InfoBox
              icon={InfoIcon}
              containerStyle={{ backgroundColor: colors.WHITE, mt: 2, p: 2 }}
              title="Challenge access requires an active subscription. If you cancel before the challenge ends, you will lose access to the app."
            />
          </ContentSection>
        </ContentWrapper>
      </Popup>

      {paymentPopupVisible && !!user && (
        <Elements stripe={stripePromise}>
          <PaymentPopup
            user={user}
            setPaymentMethod={method =>
              handlePaymentMethodUpdate(method, setPaymentMethod)
            }
            setPopupVisible={setPaymentPopupVisible}
          />
        </Elements>
      )}
    </>
  );
};
